import { useCallback, useEffect, useReducer } from 'react';
import { useAnimationCache } from './useAnimationCache';

type TypedTextState = {
  currentStringIndex: number;
  cursorVisible: boolean;
  displayText: string;
  hasStarted: boolean;
  isDeleting: boolean;
  isTypingComplete: boolean;
};

type TypedTextAction =
  | { payload: number; type: 'FINISH_DELETING' }
  | { payload: string; type: 'SET_DISPLAY_TEXT' }
  | { type: 'COMPLETE' }
  | { type: 'START_DELETING' }
  | { type: 'START' }
  | { type: 'TOGGLE_CURSOR' };

const createInitialState = (startDelay: number): TypedTextState => ({
  currentStringIndex: 0,
  cursorVisible: true,
  displayText: '',
  hasStarted: startDelay === 0,
  isDeleting: false,
  isTypingComplete: false,
});

const typedTextReducer = (state: TypedTextState, action: TypedTextAction): TypedTextState => {
  switch (action.type) {
    case 'START':
      return { ...state, hasStarted: true };
    case 'SET_DISPLAY_TEXT':
      return { ...state, displayText: action.payload };
    case 'START_DELETING':
      return { ...state, isDeleting: true };
    case 'FINISH_DELETING':
      return { ...state, currentStringIndex: action.payload, isDeleting: false };
    case 'COMPLETE':
      return { ...state, isTypingComplete: true };
    case 'TOGGLE_CURSOR':
      return { ...state, cursorVisible: !state.cursorVisible };
    default:
      return state;
  }
};

type UseTypedTextOptions = {
  backSpeed?: number;
  fixedText?: string;
  loop?: boolean;
  playOnce?: boolean;
  showCursor?: boolean;
  startDelay?: number;
  strings: string[];
  typeSpeed?: number;
};

export const useTypedText = ({
  backSpeed = 30,
  fixedText,
  loop = true,
  playOnce = false,
  showCursor = true,
  startDelay = 0,
  strings,
  typeSpeed = 40,
}: UseTypedTextOptions) => {
  const {
    cacheKey,
    hasPlayed: hasPlayedOnce,
    markAsPlayed,
  } = useAnimationCache({
    enabled: playOnce,
    fixedText,
    strings,
  });

  const [state, dispatch] = useReducer(typedTextReducer, startDelay, createInitialState);

  const { currentStringIndex, cursorVisible, displayText, hasStarted, isDeleting, isTypingComplete } = state;

  // Handle start delay
  useEffect(() => {
    if (startDelay === 0 || hasStarted) return;

    const delayTimeout = setTimeout(() => {
      dispatch({ type: 'START' });
    }, startDelay);

    return () => clearTimeout(delayTimeout);
  }, [startDelay, hasStarted]);

  // Memoized animation completion handler
  const handleAnimationComplete = useCallback(() => {
    dispatch({ type: 'COMPLETE' });
    markAsPlayed();
  }, [markAsPlayed]);

  // Main typing effect
  useEffect(() => {
    if (hasPlayedOnce || !hasStarted) return;

    const currentString = strings[currentStringIndex];
    if (!currentString) return;

    const isFinishedTyping = !isDeleting && displayText === currentString;
    const isFinishedDeleting = isDeleting && displayText === '';
    const shouldContinueCycling = (!playOnce && loop) || currentStringIndex < strings.length - 1;

    // Finished typing current string
    if (isFinishedTyping) {
      if (shouldContinueCycling) {
        const timeout = setTimeout(() => dispatch({ type: 'START_DELETING' }), 2000);
        return () => clearTimeout(timeout);
      }
      handleAnimationComplete();
      return;
    }

    // Finished deleting, move to next string
    if (isFinishedDeleting) {
      const nextIndex = (currentStringIndex + 1) % strings.length;
      const timeout = setTimeout(() => dispatch({ payload: nextIndex, type: 'FINISH_DELETING' }), 0);
      return () => clearTimeout(timeout);
    }

    // Type or delete next character
    const nextText = isDeleting
      ? currentString.substring(0, displayText.length - 1)
      : currentString.substring(0, displayText.length + 1);

    const timeout = setTimeout(
      () => {
        dispatch({ payload: nextText, type: 'SET_DISPLAY_TEXT' });
      },
      isDeleting ? backSpeed : typeSpeed,
    );

    return () => clearTimeout(timeout);
  }, [
    displayText,
    currentStringIndex,
    isDeleting,
    strings,
    typeSpeed,
    backSpeed,
    loop,
    hasStarted,
    hasPlayedOnce,
    handleAnimationComplete,
  ]);

  // Cursor blink effect
  useEffect(() => {
    if (!showCursor) return;
    const interval = setInterval(() => {
      dispatch({ type: 'TOGGLE_CURSOR' });
    }, 530);
    return () => clearInterval(interval);
  }, [showCursor]);

  const shouldShowCursor = showCursor && (!isTypingComplete || loop) && cursorVisible;
  const finalText = strings[strings.length - 1] || strings[0];

  return {
    cacheKey,
    displayText,
    finalText,
    fixedText,
    hasPlayedOnce,
    hasStarted,
    isTypingComplete,
    playOnce,
    shouldShowCursor,
  };
};
